<template>
  <FriedModal :closable="false">
    <template #header>
      <FriedModalHeader
        :header="$t('sender-requested-attachment')"
        :description="
          $t('sender_requested_attachment_description_v2', { fileName: requestedFileName })
        "
      />
    </template>
    <FriedDropzone
      data-external="upload-attachment-upload-file-area-btn"
      :accepted-file-types="acceptedFileTypes"
      @update:model-value="handleSelectFile"
    >
      <template #title>
        <FriedH4>{{ fileName }}</FriedH4>
      </template>
    </FriedDropzone>
    <FriedMessage
      v-if="failed"
      :type="MessageType.Error"
      :title="$t('failed-to-upload-attachment')"
    />
    <FriedProgressBar
      v-if="status"
      class="upload-attachment-progress-bar"
      :value="status"
      :error="failed"
      :completed="status === 100"
    />
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          data-external="base-modal-buttons-footer-cancel-btn"
          :button-type="ButtonType.Secondary"
          :disabled="actionsPrevented"
          @click="$emit('close')"
        >
          {{ $t('cancel') }}
        </FriedButton>
        <FriedButton
          data-external="base-modal-buttons-footer-upload-btn"
          class="upload-button"
          :disabled="!file"
          :loading="actionsPrevented"
          @click="upload"
        >
          {{ $t('upload') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModal>
</template>
<script lang="ts">
import { ButtonType, MessageType } from '@getaccept/fried-tofu';
import { HeaderHelper } from '@getaccept/lib-shared-new/src/helpers/header.helper';
import { computed, defineComponent, ref } from 'vue';
import { allAcceptedFileTypes } from '@getaccept/lib-shared-new/src/file/constants/accepted-file-types';
import { useI18n } from 'vue-i18n';
import { UploadAttachmentStatus } from '../types/enums/upload-attachment-status';
import { AttachmentService } from '../../../api/attachments/services/attachment.service';

export default defineComponent({
  props: {
    attachmentId: { type: String, default: '' },
    requestedFileName: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const file = ref<File>(null);
    const status = ref<UploadAttachmentStatus>(null);
    const failed = ref(false);

    const actionsPrevented = computed(() => !!status.value && !failed.value);

    const fileName = computed(() =>
      file.value ? file.value?.name || '' : t('click-or-drop-files-here-to-upload')
    );

    const handleSelectFile = (newFiles: File[]) => {
      file.value = newFiles[0];
    };

    const upload = async () => {
      if (!file.value) {
        return;
      }

      failed.value = false;
      status.value = UploadAttachmentStatus.GettingUploadLink;
      const fileExtension: string = file.value?.name.split('.').pop();

      try {
        const {
          url,
          headers,
        }: { url: string; headers: string } = await AttachmentService.getUploadLink(
          props.attachmentId,
          fileExtension
        );

        status.value = UploadAttachmentStatus.UploadingAttachment;
        const formattedHeaders: Record<string, string> = HeaderHelper.formatHeaders(
          headers,
          file.value.type
        );
        await AttachmentService.upload(file.value, url, formattedHeaders);

        status.value = UploadAttachmentStatus.Finalizing;
        await AttachmentService.uploadDone(props.attachmentId, file.value?.name);

        emit('uploaded');
      } catch (e) {
        failed.value = true;
      } finally {
        status.value = UploadAttachmentStatus.Completed;
      }
    };

    return {
      file,
      status,
      failed,
      actionsPrevented,
      fileName,
      handleSelectFile,
      upload,
      ButtonType,
      MessageType,
      acceptedFileTypes: allAcceptedFileTypes.toString(),
    };
  },
});
</script>
<style lang="scss" scoped>
.upload-attachment-progress-bar {
  margin: var(--spacing-50) 0;
}
</style>
